/**
 * useAppLanguage — LA langue d'interface de l'APP CONNECTÉE.
 *
 * Avant ce fichier, l'app authentifiée lisait sa langue de QUATRE endroits
 * différents, qui ne disaient pas la même chose :
 *
 *   · `useUiLanguage` interrogeait `localStorage.archer_language` — une clé
 *     que PERSONNE n'écrit. Le site public écrit `archer_locale`. La barre
 *     latérale et la page dossier retombaient donc systématiquement sur le
 *     défaut de juridiction, c'est-à-dire le français, quelle que soit la
 *     langue choisie sur le site.
 *   · `Cases`, `AnalyzeDocument`, `LegalChat` lisaient `user.language`,
 *     figé au moment de l'inscription et jamais rafraîchi à chaud.
 *   · `Upload` dérivait un booléen `isFr` — donc pas de néerlandais du tout.
 *   · `BureauV2`, les modales et la cinématique n'avaient aucune lecture :
 *     français en dur.
 *
 * Résultat concret : un néerlandophone qui choisissait NL sur le site,
 * s'inscrivait et arrivait sur son bureau voyait une app française.
 *
 * Ici, UNE seule source : `archer_locale`, exactement le store du site
 * public, lu par le même hook (`useSiteLanguage`). Choisir NL sur la page
 * d'accueil, c'est déjà avoir choisi NL pour son bureau — il n'y a plus
 * deux réglages à garder d'accord, il n'y en a qu'un.
 *
 * `user.language` n'est pas ignoré pour autant : il reste la mémoire du
 * compte côté serveur, celle qui traverse les appareils. Mais il ne SERT
 * qu'à amorcer le store quand celui-ci est vide (`seedLanguageFromUser`,
 * appelé une fois à la connexion). Une fois amorcé, le choix local prime :
 * l'utilisateur qui bascule le sélecteur veut voir l'effet tout de suite,
 * pas au prochain aller-retour serveur.
 */
import { useCallback } from 'react';
import {
  useSiteLanguage,
  readSiteLanguage,
  writeSiteLanguage,
  SITE_LANGUAGES,
  DEFAULT_SITE_LANGUAGE,
} from './useSiteLanguage';

export { SITE_LANGUAGES, DEFAULT_SITE_LANGUAGE, readSiteLanguage, writeSiteLanguage };

/** Ramène 'fr-BE', 'NL', 'nl-be' … à 'fr' | 'nl' | 'en'. */
export function normalizeLang(value) {
  const short = String(value || '').slice(0, 2).toLowerCase();
  return SITE_LANGUAGES.includes(short) ? short : DEFAULT_SITE_LANGUAGE;
}

/**
 * Vrai tant que l'utilisateur n'a JAMAIS choisi de langue sur cet appareil.
 * `readSiteLanguage` ne permet pas de faire la différence : il rend 'fr'
 * aussi bien pour « rien de stocké » que pour « français choisi ». On lit
 * donc la clé brute, seule façon de distinguer un défaut d'un choix.
 */
function hasStoredChoice() {
  try {
    return localStorage.getItem('archer_locale') != null;
  } catch (_) {
    return false;
  }
}

/**
 * Amorce le store local depuis la langue du compte, et seulement si rien
 * n'a encore été choisi sur cet appareil.
 *
 * Le cas qu'il couvre : un utilisateur néerlandophone qui se connecte
 * depuis un autre navigateur. Son `archer_locale` est vide ici, mais son
 * compte porte `language: 'nl'` depuis l'inscription. Sans cet amorçage il
 * atterrirait en français sur une machine neuve.
 *
 * La garde `hasStoredChoice` est ce qui empêche l'inverse : écraser à
 * chaque montage un choix local récent par la valeur serveur, plus
 * ancienne. Le serveur amorce, il ne corrige pas.
 */
export function seedLanguageFromUser(user) {
  const fromAccount = user?.language;
  if (!fromAccount || hasStoredChoice()) return null;
  return writeSiteLanguage(normalizeLang(fromAccount));
}

/**
 * Langue courante de l'app, tenue à jour à chaud (même événement que le
 * site public : changer la langue dans les Paramètres re-rend la barre
 * latérale, le bureau et la page dossier sans rechargement).
 *
 * Retourne `[lang, setLang]`, exactement comme `useSiteLanguage`.
 */
export function useAppLanguage() {
  const [lang, setLang] = useSiteLanguage();
  const set = useCallback((next) => setLang(normalizeLang(next)), [setLang]);
  return [lang, set];
}

export default useAppLanguage;
